import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Typography,
  Tabs,
  Tab,
  alpha,
} from '@mui/material';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import FunctionsOutlinedIcon from '@mui/icons-material/FunctionsOutlined';
import { getSampleDisplay } from '../../../common/functionMode';
import {
  isArgsOutputMode,
  resolveAssertOn,
} from '../../../common/functionModeTypes';
import { formatSignatureLine } from '../../../common/functionModeNaming';
import {
  formatExecutionLimitsLabel,
  resolveLimitsForDisplay,
} from '../../../common/executionLimits';
import { ACCENT, difficultyColor, useAdminPageTheme } from '../../../pages/admin/adminUi';

const PREVIEW_LANGS = ['python', 'javascript', 'java'];

const monoSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: '0.78rem',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const Section = ({ title, children }) => {
  const { border, surface } = useAdminPageTheme();
  if (!children) return null;
  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 2,
        border: `1px solid ${border}`,
        bgcolor: surface,
      }}
    >
      <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', mb: 1 }}>{title}</Typography>
      {children}
    </Box>
  );
};

const HtmlBlock = ({ html }) => {
  const { muted } = useAdminPageTheme();
  const text = String(html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  if (!text) {
    return (
      <Typography variant="body2" sx={{ color: muted, fontStyle: 'italic' }}>
        Not provided yet
      </Typography>
    );
  }
  return (
    <Box
      className="problem-preview-html"
      sx={{
        '& p': { m: 0, mb: 1 },
        '& ul, & ol': { pl: 2.5, my: 1 },
        fontSize: '0.875rem',
        lineHeight: 1.55,
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const SampleCaseCard = ({ sample, index, isFunction, signature }) => {
  const { border, muted, isDark } = useAdminPageTheme();
  const display = getSampleDisplay(sample, signature);

  return (
    <Box
      sx={{
        p: 1.5,
        mb: 1,
        borderRadius: 1.5,
        border: `1px solid ${border}`,
        bgcolor: isDark ? alpha('#fff', 0.02) : '#fafbfc',
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: muted, mb: 1 }}>
        Example {index + 1}
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', mb: 0.75 }}>
        <strong>Input:</strong>{' '}
        <Box component="span" sx={monoSx}>
          {display.input || '(empty)'}
        </Box>
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', mb: sample.explanation ? 0.75 : 0 }}>
        <strong>Output:</strong>{' '}
        <Box component="span" sx={monoSx}>
          {display.output || '(empty)'}
        </Box>
      </Typography>
      {sample.explanation && (
        <Typography sx={{ fontSize: '0.8rem', color: muted }}>
          <strong>Explanation:</strong> {sample.explanation}
        </Typography>
      )}
      {!isFunction && !display.input && !display.output && (
        <Typography variant="caption" sx={{ color: muted }}>
          Add input/output in the Tests tab
        </Typography>
      )}
      {isFunction && sample.args == null && (
        <Typography variant="caption" sx={{ color: muted }}>
          Fill args and expected values in the Tests tab
        </Typography>
      )}
    </Box>
  );
};

/**
 * Live preview of how the problem will appear to students (admin create/edit form).
 */
const ProblemFormPreview = ({
  title,
  description,
  difficulty,
  constraints,
  inputFormat,
  outputFormat,
  topics,
  section,
  level,
  mode,
  signature,
  testCases,
  stubHint,
  starterCode,
  executionLimits,
  starterOptions,
}) => {
  const { muted, border, isDark } = useAdminPageTheme();
  const [previewLang, setPreviewLang] = useState('python');

  const problemLike = useMemo(
    () => ({
      mode,
      difficulty,
      executionLimits,
      stubHint,
    }),
    [mode, difficulty, executionLimits, stubHint],
  );

  const isFunction = mode === 'function';
  const samples = (testCases || []).filter((tc) => tc.isSample);
  const limitsLabel = formatExecutionLimitsLabel(resolveLimitsForDisplay(problemLike));

  const signatureLine = useMemo(() => {
    if (!isFunction || !signature?.methodName) return null;
    return formatSignatureLine(signature, previewLang);
  }, [isFunction, signature, previewLang]);

  const codePreview = starterCode?.[previewLang];
  const hasCode = Boolean(codePreview?.trim());

  return (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="h5" sx={{ fontWeight: 900, mb: 1, letterSpacing: '-0.02em' }}>
        {title?.trim() || 'Untitled problem'}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2, alignItems: 'center' }}>
        <Chip
          label={difficulty || 'easy'}
          size="small"
          sx={{
            fontWeight: 800,
            color: difficultyColor(difficulty),
            bgcolor: alpha(difficultyColor(difficulty), 0.12),
          }}
        />
        <Chip
          size="small"
          icon={isFunction ? <FunctionsOutlinedIcon /> : <CodeOutlinedIcon />}
          label={isFunction ? 'Function mode' : 'IO mode'}
          sx={{ fontWeight: 700 }}
        />
        {limitsLabel && (
          <Chip label={limitsLabel} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
        )}
        {section && <Chip label={section} size="small" variant="outlined" />}
        {level != null && level !== '' && (
          <Chip label={`Level ${level}`} size="small" variant="outlined" />
        )}
      </Box>

      {(topics || []).length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          {topics.map((t) => (
            <Chip key={t} label={t} size="small" sx={{ fontWeight: 600, fontSize: '0.72rem' }} />
          ))}
        </Box>
      )}

      {isFunction && stubHint?.trim() && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 1.5,
            border: `1px solid ${alpha(ACCENT, 0.35)}`,
            bgcolor: alpha(ACCENT, isDark ? 0.12 : 0.07),
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: ACCENT, mb: 0.5 }}>
            Code editor hint
          </Typography>
          <Box
            sx={{ fontSize: '0.82rem', color: muted, lineHeight: 1.45 }}
            dangerouslySetInnerHTML={{ __html: stubHint }}
          />
        </Box>
      )}

      <Section title="Description">
        <HtmlBlock html={description} />
      </Section>

      {(constraints || '').replace(/<[^>]*>/g, '').trim() && (
        <Section title="Constraints">
          <HtmlBlock html={constraints} />
        </Section>
      )}

      {(inputFormat?.trim() || outputFormat?.trim()) && (
        <Section title="Input / output format">
          {inputFormat?.trim() && (
            <Box sx={{ mb: outputFormat?.trim() ? 1.5 : 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', mb: 0.5 }}>Input</Typography>
              <Typography sx={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{inputFormat}</Typography>
            </Box>
          )}
          {outputFormat?.trim() && (
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', mb: 0.5 }}>Output</Typography>
              <Typography sx={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{outputFormat}</Typography>
            </Box>
          )}
        </Section>
      )}

      {isFunction && signatureLine && (
        <Section title="Method signature">
          <Box
            sx={{
              ...monoSx,
              p: 1.25,
              borderRadius: 1,
              bgcolor: isDark ? '#0d1117' : '#f6f8fa',
              border: `1px solid ${border}`,
            }}
          >
            {signatureLine}
          </Box>
          {signature?.methodNotes?.trim() && (
            <Typography sx={{ mt: 1, fontSize: '0.82rem', color: muted, fontStyle: 'italic' }}>
              {signature.methodNotes}
            </Typography>
          )}
          {isArgsOutputMode(signature) && (
            <Typography variant="caption" sx={{ color: muted, display: 'block', mt: 1 }}>
              Judging checks mutated params after call: {resolveAssertOn(signature).join(', ') || '(none)'}
            </Typography>
          )}
          {starterOptions?.includeBuiltinStructs !== false && (
            <Typography variant="caption" sx={{ color: muted, display: 'block', mt: 1 }}>
              ListNode / TreeNode helpers are auto-included in the starter when used in the signature.
            </Typography>
          )}
        </Section>
      )}

      <Section title={`Examples (${samples.length})`}>
        {samples.length === 0 ? (
          <Typography variant="body2" sx={{ color: muted, fontStyle: 'italic' }}>
            Mark at least one test case as &quot;Sample&quot; to show examples here.
          </Typography>
        ) : (
          samples.map((sample, index) => (
            <SampleCaseCard key={index} sample={sample} index={index} isFunction={isFunction} signature={signature} />
          ))
        )}
        {(testCases || []).length > samples.length && (
          <Typography variant="caption" sx={{ color: muted }}>
            + {(testCases || []).length - samples.length} hidden test case(s) not shown to students
          </Typography>
        )}
      </Section>

      <Section title="Starter code preview">
        <Tabs
          value={previewLang}
          onChange={(_, v) => setPreviewLang(v)}
          sx={{ minHeight: 36, mb: 1, borderBottom: `1px solid ${border}` }}
        >
          {PREVIEW_LANGS.map((lang) => (
            <Tab
              key={lang}
              value={lang}
              label={lang}
              sx={{ minHeight: 36, textTransform: 'none', fontWeight: 700, fontSize: '0.78rem' }}
            />
          ))}
        </Tabs>
        {hasCode ? (
          <Box
            component="pre"
            sx={{
              ...monoSx,
              m: 0,
              p: 1.5,
              maxHeight: 280,
              overflow: 'auto',
              borderRadius: 1,
              bgcolor: isDark ? '#0d1117' : '#f6f8fa',
              border: `1px solid ${border}`,
            }}
          >
            {codePreview}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: muted, fontStyle: 'italic' }}>
            {isFunction
              ? 'Starter code is auto-generated when you save. Override per language under Tests & code if needed.'
              : 'Add wrapper code per language under Tests & code.'}
          </Typography>
        )}
      </Section>
    </Box>
  );
};

export default ProblemFormPreview;
